import axios from 'axios';

// API 기본 설정
const API_BASE_URL = process.env.REACT_APP_CAMPUS_API_BASE_URL || 'https://campus-dev.duckdns.org';

// axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
});

// 토큰 갱신 진행 중인지 확인하는 플래그
let isRefreshing = false;
let failedQueue: Array<{ resolve: Function; reject: Function }> = [];
let refreshPromise: Promise<string | null> | null = null;

// 실패한 요청들을 큐에서 처리하는 함수
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

// 단일 실행 보장: 동시에 여러 요청이 와도 refresh는 1번만 수행
const startRefresh = async (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise;
  
  // 로컬 환경 체크
  const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  if (isLocal) {
    console.log('로컬 환경에서는 토큰 갱신을 건너뜁니다.');
    return localStorage.getItem('access_token');
  }
  
  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const { refreshToken } = await import('../auth/authApi');
      const refreshResponse = await refreshToken();
      const newToken = refreshResponse.data.access_token || null;
      processQueue(null, newToken);
      return newToken;
    } catch (e) {
      processQueue(e, null);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

// 요청 인터셉터 - 모든 요청 전에 토큰 확인/갱신 시도 후 자동 첨부
apiClient.interceptors.request.use(
  async (config) => {
    const url = config.url || '';
    const skipRefreshPaths = ['/auth/refresh', '/auth/sign-in', '/auth/public-key'];
    const shouldSkip = skipRefreshPaths.some((p) => url.includes(p));

    // 로그인/공개키/리프레시 요청은 갱신 스킵
    if (!shouldSkip) {
      if (isRefreshing || refreshPromise) {
        // 이미 갱신 중 → 큐에서 기다렸다가 토큰을 받아 헤더에 세팅
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (newToken: string | null) => {
              if (newToken && config.headers) {
                config.headers.Authorization = `Bearer ${newToken}`;
              }
              resolve(config);
            },
            reject,
          });
        });
      }
      const newToken = await startRefresh();
      if (newToken && config.headers) {
        config.headers.Authorization = `Bearer ${newToken}`;
      }
    } else {
      // 스킵 대상은 기존 토큰만 있으면 부착
      const token = localStorage.getItem('access_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 자동 토큰 갱신
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 네트워크 에러 처리
    if (!error.response) {
      console.error('네트워크 에러:', error.message);
      return Promise.reject(new Error('네트워크 연결을 확인해주세요.'));
    }
    
    // 401 에러이고 토큰 갱신 요청이 아닌 경우 토큰 갱신 시도
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh')) {
      
      // 로컬 환경에서는 토큰 갱신을 시도하지 않음
      // const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
      // if (isLocal) {
      //   console.log('로컬 환경에서 401 에러 발생, 토큰 갱신을 건너뜀고 에러를 그대로 반환합니다.');
      //   return Promise.reject(error);
      // }
      
      if (isRefreshing) {
        // 이미 토큰 갱신 중이면 큐에 추가하고 기다림
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('401 에러 발생, 토큰 갱신 시도 중...');
        
        // refreshToken 함수를 동적으로 import (순환 참조 방지)
        const { refreshToken } = await import('../auth/authApi');
        const refreshResponse = await refreshToken();
        const newToken = refreshResponse.data.access_token;
        
        if (newToken) {
          // 새로운 토큰으로 원래 요청 재시도
          originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
          processQueue(null, newToken);
          
          console.log('토큰 갱신 성공, 원래 요청 재시도');
          return apiClient(originalRequest);
        } else {
          throw new Error('새로운 액세스 토큰을 받지 못했습니다.');
        }
      } catch (refreshError) {
        console.error('토큰 갱신 실패:', refreshError);
        processQueue(refreshError, null);
        
        // 토큰 갱신 실패 시 로그인 페이지로 리다이렉트
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // 기타 HTTP 상태 코드별 처리
    if (error.response?.status === 403) {
      console.error('접근 권한이 없습니다.');
    } else if (error.response?.status >= 500) {
      console.error('서버 에러가 발생했습니다.');
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
export { API_BASE_URL };
